import Lightbox from '@/components/Lightbox';
import { PhotoAnnotatorModal } from '@/components_v2/PhotoAnnotator';
import { DiscussionTopic } from '@/hooks/Feedback/interfaces';
import { CommentMediaInput } from '@/hooks/Feedback/services/createFeedbackComment';
import { useFeedbackCommentMutation } from '@/hooks/Feedback/useFeedbackComment';
import { useFeedbackCommentsQuery } from '@/hooks/Feedback/useFeedbackCommentsQuery';
import { formularyStorage } from '@/modules/shared/adapters/CloudStorage';
import { blobToFile } from '@/utils/blobToFile';
import { formatFileSize } from '@/utils/formatFileSize';
import { readFile } from '@/utils/readFile';
import { AttachmentIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Circle,
  Flex,
  Icon,
  IconButton,
  Image,
  List,
  ListItem,
  Progress,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { orderBy } from 'lodash';
import React from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import Scrollbars from 'react-custom-scrollbars';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiX } from 'react-icons/fi';
import { GrDrag } from 'react-icons/gr';
import { v4 as uuidV4 } from 'uuid';

import { ActivityItem } from './ActivityItem';
import FeedbackQuestionPreview from './FeedbackQuestionPreview';
import {
  ALLOWED_UPLOAD_EXTENSIONS,
  getFileChipConfig,
  getMediaKind,
  UploadCategory,
  validateUploadFile,
} from './mediaType';

const FILE_INPUT_ACCEPT = ALLOWED_UPLOAD_EXTENSIONS.map(
  (ext) => `.${ext}`,
).join(',');

// Espaçamento entre positions — deixa margem para reordenar mídias já
// persistidas no futuro (média entre vizinhos) sem recalcular todo o resto.
const POSITION_STEP = 10000;

const TOO_LARGE_KEY: Record<UploadCategory, string> = {
  image: 'error.fileTooLargeImage',
  video: 'error.fileTooLargeVideo',
  audio: 'error.fileTooLargeAudio',
  document: 'error.fileTooLargeDocument',
};

type FeedbackActivitiesProps = {
  feedback: DiscussionTopic;
};

type PendingAttachment = {
  tempId: string;
  file: File;
  fileName: string;
  progress: number;
  url?: string;
  error?: string;
};

type QueuedAnnotation = {
  queueId: string;
  file: File;
};

type CommentComposeProps = {
  handleSubmit: (payload: {
    content: string;
    media: CommentMediaInput[];
  }) => void;
};

const ComentCompose: React.FC<CommentComposeProps> = ({ handleSubmit }) => {
  const { t, i18n } = useTranslation();
  const [content, setContent] = React.useState<string>('');
  const [attachments, setAttachments] = React.useState<PendingAttachment[]>([]);
  const [annotationQueue, setAnnotationQueue] = React.useState<
    QueuedAnnotation[]
  >([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const currentAnnotation = annotationQueue[0] ?? null;
  const [currentAnnotationUrl, setCurrentAnnotationUrl] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    if (!currentAnnotation) {
      setCurrentAnnotationUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(currentAnnotation.file);
    setCurrentAnnotationUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [currentAnnotation]);

  const hasInvalidAttachment = attachments.some((a) => !!a.error);
  // Chips com erro nunca terão url — precisam sair do cálculo de isUploading,
  // senão o botão (e o Enter) ficam travados para sempre, até para texto puro.
  const isUploading = attachments.some((a) => !a.error && a.url === undefined);
  const canSubmit =
    !isUploading &&
    !hasInvalidAttachment &&
    (content.trim().length > 0 || attachments.some((a) => !!a.url));

  const onSubmit = React.useCallback(() => {
    if (!canSubmit) return;

    const media: CommentMediaInput[] = attachments
      .filter((a): a is PendingAttachment & { url: string } => !!a.url)
      .map((a, index) => ({
        content: a.url,
        fileName: a.fileName,
        fileSize: a.file.size,
        position: (index + 1) * POSITION_STEP,
      }));

    setContent('');
    setAttachments([]);
    handleSubmit({ content: content.trim(), media });
  }, [attachments, canSubmit, content, handleSubmit]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSubmit();
      }
    },
    [onSubmit],
  );

  const attachFile = React.useCallback((file: File) => {
    const tempId = uuidV4();
    setAttachments((prev) => [
      ...prev,
      { tempId, file, fileName: file.name, progress: 0 },
    ]);

    formularyStorage.uploadFile({
      file,
      setProgress: (progress) => {
        setAttachments((prev) =>
          prev.map((a) => (a.tempId === tempId ? { ...a, progress } : a)),
        );
      },
      setFileDownloadUrl: (url) => {
        setAttachments((prev) => {
          // Ignorar se o item já foi removido pelo usuário
          if (!prev.find((a) => a.tempId === tempId)) return prev;
          return prev.map((a) =>
            a.tempId === tempId ? { ...a, url, progress: 100 } : a,
          );
        });
      },
    });
  }, []);

  const handleFiles = React.useCallback(
    (files: FileList | null) => {
      if (!files?.length) return;
      Array.from(files).forEach((file) => {
        const validation = validateUploadFile(file);

        if (!validation.ok) {
          const tempId = uuidV4();
          let errorMessage: string;
          if (validation.reason === 'type') {
            errorMessage = t('error.invalidFileType');
          } else if (validation.reason === 'empty') {
            errorMessage = t('error.emptyFile');
          } else {
            errorMessage = t(TOO_LARGE_KEY[validation.category], {
              limit: formatFileSize(validation.maxBytes, i18n.language),
              size: formatFileSize(file.size, i18n.language),
            });
          }

          // Erro por arquivo: nenhum byte sobe pro Firebase, mas os demais
          // arquivos do lote seguem seu fluxo normal.
          setAttachments((prev) => [
            ...prev,
            {
              tempId,
              file,
              fileName: file.name,
              progress: 0,
              error: errorMessage,
            },
          ]);
          return;
        }

        // Imagens passam pelo preview de anotação antes de subir; os demais
        // tipos seguem direto para o upload, como antes.
        if (getMediaKind(file.name) === 'image') {
          setAnnotationQueue((prev) => [...prev, { queueId: uuidV4(), file }]);
          return;
        }

        attachFile(file);
      });
    },
    [t, i18n.language, attachFile],
  );

  const handleAnnotationConfirm = React.useCallback(
    async (dataUrl: string | null) => {
      if (!currentAnnotation) return;
      if (dataUrl) {
        const blob = await readFile(dataUrl);
        const file = await blobToFile(blob, currentAnnotation.file.name);
        attachFile(file);
      } else {
        attachFile(currentAnnotation.file);
      }
      setAnnotationQueue((prev) => prev.slice(1));
    },
    [currentAnnotation, attachFile],
  );

  const handleAnnotationCancel = React.useCallback(() => {
    setAnnotationQueue((prev) => prev.slice(1));
  }, []);

  const removeAttachment = React.useCallback((tempId: string) => {
    setAttachments((prev) => prev.filter((a) => a.tempId !== tempId));
  }, []);

  const handleDragEnd = React.useCallback((result: DropResult) => {
    const { destination, source } = result;
    if (!destination || source.index === destination.index) return;

    setAttachments((prev) => {
      const next = [...prev];
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      return next;
    });
  }, []);

  return (
    <Flex
      zIndex="2"
      borderWidth="thin"
      borderColor="gray.200"
      rounded="lg"
      bg="white"
      flexDir="column"
      p="3"
      boxShadow="2xl"
      gap="2"
    >
      {/* Pré-visualização de anexos */}
      {attachments.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="feedback-attachments" direction="horizontal">
            {(droppableProvided, droppableSnapshot) => (
              <Wrap
                spacing="2"
                p="1"
                borderWidth="1px"
                borderStyle="dashed"
                borderRadius="10px"
                borderColor={
                  droppableSnapshot.isDraggingOver ? 'gray.300' : 'transparent'
                }
                bg={
                  droppableSnapshot.isDraggingOver ? 'gray.100' : 'transparent'
                }
                transition="background-color 0.15s, border-color 0.15s"
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {attachments.map((att, index) => {
                  const kind = getMediaKind(att.fileName);
                  const isDone = att.url !== undefined;
                  const showDragHandle = attachments.length > 1;

                  return (
                    <Draggable
                      key={att.tempId}
                      draggableId={att.tempId}
                      index={index}
                      isDragDisabled={!!att.error}
                    >
                      {(dragProvided, dragSnapshot) => {
                        if (att.error) {
                          const chipConfig = getFileChipConfig(
                            kind,
                            att.fileName,
                          );
                          return (
                            <WrapItem
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              w="full"
                            >
                              <Flex
                                align="center"
                                gap="3"
                                p="2"
                                borderWidth="thin"
                                borderColor="red.200"
                                borderRadius="lg"
                                bg="red.50"
                                w="full"
                              >
                                <Center
                                  w="10"
                                  h="10"
                                  borderRadius="md"
                                  bg={chipConfig.bg}
                                  flexShrink={0}
                                >
                                  <Icon
                                    as={FiAlertTriangle}
                                    color="white"
                                    fontSize="18px"
                                  />
                                </Center>
                                <Box flex="1" minW="0">
                                  <Text
                                    fontSize="sm"
                                    fontWeight="medium"
                                    wordBreak="break-word"
                                  >
                                    {att.fileName}
                                  </Text>
                                  <Text fontSize="xs" color="red.500" mt="0.5">
                                    {att.error}
                                  </Text>
                                </Box>
                                <Circle
                                  as="button"
                                  size="5"
                                  bg="gray.300"
                                  flexShrink={0}
                                  cursor="pointer"
                                  onClick={() => removeAttachment(att.tempId)}
                                  aria-label={t('common.remove')}
                                >
                                  <Icon
                                    as={FiX}
                                    color="gray.600"
                                    fontSize="10px"
                                  />
                                </Circle>
                              </Flex>
                            </WrapItem>
                          );
                        }

                        return (
                          <WrapItem
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                          >
                            <Box
                              position="relative"
                              w="24"
                              h="24"
                              boxShadow={
                                dragSnapshot.isDragging ? 'lg' : 'none'
                              }
                              transform={
                                dragSnapshot.isDragging
                                  ? 'scale(1.04)'
                                  : undefined
                              }
                              borderRadius="md"
                              transition="box-shadow 0.15s, transform 0.15s"
                            >
                              {/* Botão remover */}
                              <Circle
                                as="button"
                                size="4"
                                bg="blackAlpha.600"
                                position="absolute"
                                top="0.5"
                                right="0.5"
                                zIndex="1"
                                cursor="pointer"
                                onClick={() => removeAttachment(att.tempId)}
                                aria-label={t('common.remove')}
                              >
                                <Icon as={FiX} color="white" fontSize="10px" />
                              </Circle>
                              {showDragHandle && (
                                <Tooltip label={t('common.drag_to_reorder')}>
                                  <Circle
                                    size="4"
                                    bg="blackAlpha.600"
                                    position="absolute"
                                    bottom="0.5"
                                    right="0.5"
                                    zIndex="1"
                                    cursor="grab"
                                    _active={{ cursor: 'grabbing' }}
                                  >
                                    <Icon
                                      as={GrDrag}
                                      color="white"
                                      fontSize="10px"
                                    />
                                  </Circle>
                                </Tooltip>
                              )}
                              {/* Thumbnail */}
                              {kind === 'image' ? (
                                <Image
                                  src={URL.createObjectURL(att.file)}
                                  w="24"
                                  h="24"
                                  objectFit="cover"
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderStyle="solid"
                                  borderColor="gray.300"
                                  opacity={isDone ? 1 : 0.5}
                                  cursor={isDone ? 'zoom-in' : 'default'}
                                  onClick={
                                    isDone
                                      ? () =>
                                          Lightbox.openLightbox(
                                            [URL.createObjectURL(att.file)],
                                            0,
                                          )
                                      : undefined
                                  }
                                />
                              ) : (
                                <Flex
                                  w="24"
                                  h="24"
                                  flexDir="column"
                                  align="center"
                                  justify="center"
                                  borderWidth="thin"
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  bg="gray.50"
                                  overflow="hidden"
                                  opacity={isDone ? 1 : 0.7}
                                  p="2"
                                  gap="1"
                                  cursor="grab"
                                  _active={{ cursor: 'grabbing' }}
                                >
                                  {(() => {
                                    const chipConfig = getFileChipConfig(
                                      getMediaKind(att.fileName),
                                      att.fileName,
                                    );
                                    return (
                                      <Center
                                        w="8"
                                        h="8"
                                        borderRadius="sm"
                                        bg={chipConfig.bg}
                                      >
                                        <Icon
                                          as={chipConfig.icon}
                                          color="white"
                                          fontSize="16px"
                                        />
                                      </Center>
                                    );
                                  })()}
                                  <Text
                                    fontSize="9px"
                                    textAlign="center"
                                    noOfLines={1}
                                    title={att.fileName}
                                  >
                                    {att.fileName}
                                  </Text>
                                  <Text
                                    fontSize="9px"
                                    textAlign="center"
                                    color="gray.500"
                                    textTransform="uppercase"
                                  >
                                    {att.fileName.split('.').pop()}
                                  </Text>
                                </Flex>
                              )}
                              {/* Barra de progresso para documentos */}
                              {!isDone && kind !== 'image' && (
                                <Progress
                                  value={att.progress}
                                  size="xs"
                                  colorScheme="brand"
                                  position="absolute"
                                  bottom="0"
                                  left="0"
                                  right="0"
                                  borderBottomRadius="md"
                                />
                              )}
                            </Box>
                          </WrapItem>
                        );
                      }}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </Wrap>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Linha de texto + ações */}
      <Flex flexDir="row" align="flex-end" gap="2">
        <Box flex="1">
          <Textarea
            placeholder={t('common.comment_here')}
            resize="none"
            variant="unstyled"
            value={content}
            onKeyDown={handleKeyDown}
            onChange={(e) => setContent(e.target.value)}
            minH="10"
          />
        </Box>

        {/* Botão de anexar */}
        <Tooltip label={t('common.attach_files')}>
          <IconButton
            aria-label={t('common.attach_files')}
            icon={<AttachmentIcon fontSize="18px" />}
            variant="outline"
            colorScheme="gray"
            size="sm"
            alignSelf="flex-end"
            bg="gray.100"
            onClick={() => fileInputRef.current?.click()}
          />
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={FILE_INPUT_ACCEPT}
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
          // Limpa o valor para permitir re-selecionar o mesmo arquivo
          onClick={(e) => {
            (e.target as HTMLInputElement).value = '';
          }}
        />

        <Tooltip
          label={t('common.remove_invalid_files')}
          isDisabled={!hasInvalidAttachment}
          shouldWrapChildren
        >
          <Button
            onClick={onSubmit}
            alignSelf="flex-end"
            colorScheme="brand"
            isDisabled={!canSubmit}
            opacity="1"
          >
            {t('common.comment_action')}
          </Button>
        </Tooltip>
      </Flex>

      <PhotoAnnotatorModal
        isOpen={!!currentAnnotation}
        photoSrc={currentAnnotationUrl}
        onClose={handleAnnotationCancel}
        onConfirm={handleAnnotationConfirm}
      />
    </Flex>
  );
};

export const FeedbackActivities: React.FC<FeedbackActivitiesProps> = ({
  feedback,
}) => {
  const scrollRef = React.useRef<Scrollbars>(null);
  const previousScrollHeightRef = React.useRef<number>(0);
  const isLoadingOlderRef = React.useRef(false);

  const {
    data,
    fetchPreviousPage,
    hasPreviousPage,
    isFetchingPreviousPage,
  } = useFeedbackCommentsQuery(feedback.id, {
    enabled: true,
    // Acelera polling enquanto há mídia pendente de processamento
    refetchInterval: (queryData) =>
      queryData?.pages
        ?.flat()
        .some((c) =>
          c.medias?.some((m) => !m.mediaProcessed && !m.hasErrorInMedia),
        )
        ? 5000
        : 30000,
  });

  const { createComment } = useFeedbackCommentMutation();

  const addComment = React.useCallback(
    async ({
      content,
      media,
    }: {
      content: string;
      media: CommentMediaInput[];
    }): Promise<void> => {
      createComment({
        content: content || undefined,
        media: media.length ? media : undefined,
        discussionTopicId: feedback.id,
      });
    },
    [createComment, feedback],
  );

  const parsedItems = React.useMemo(() => {
    const items = data?.pages?.flat() ?? [];
    if (!items.length) return [];

    return orderBy(items, 'createdAt', 'asc');
  }, [data]);

  const handleScrollFrame = React.useCallback(
    (values: { scrollTop: number }) => {
      if (
        values.scrollTop <= 40 &&
        hasPreviousPage &&
        !isFetchingPreviousPage &&
        !isLoadingOlderRef.current
      ) {
        isLoadingOlderRef.current = true;
        previousScrollHeightRef.current =
          scrollRef.current?.getScrollHeight() ?? 0;
        fetchPreviousPage();
      }
    },
    [hasPreviousPage, isFetchingPreviousPage, fetchPreviousPage],
  );

  React.useEffect(() => {
    if (!parsedItems.length) return;

    if (isLoadingOlderRef.current) {
      // Mantém a posição de leitura ao inserir comentários mais antigos no topo
      isLoadingOlderRef.current = false;
      const newScrollHeight = scrollRef.current?.getScrollHeight() ?? 0;
      scrollRef.current?.scrollTop(
        newScrollHeight - previousScrollHeightRef.current,
      );
      return;
    }

    scrollRef.current?.scrollToBottom();
  }, [parsedItems.length]);

  React.useEffect(() => {
    // Garante que a flag não fique presa caso a busca não retorne itens novos
    if (!isFetchingPreviousPage) {
      isLoadingOlderRef.current = false;
    }
  }, [isFetchingPreviousPage]);

  return (
    <Flex flex="1" overflow="hidden" flexDir="column" bg="gray.50">
      <Box px={{ base: 4, lg: 10 }} pt="6" flexShrink={0}>
        <FeedbackQuestionPreview feedback={feedback} />
      </Box>

      <Box flex="1" overflow="hidden">
        <Scrollbars ref={scrollRef} onScrollFrame={handleScrollFrame}>
          <List
            px={{ base: 4, lg: 10 }}
            w="full"
            spacing="10"
            flex="1"
            overflowY="auto"
          >
            <ListItem />
            {isFetchingPreviousPage && (
              <ListItem>
                <Center py="2">
                  <Spinner size="md" color="brand.500" thickness="3px" />
                </Center>
              </ListItem>
            )}
            {parsedItems?.map((comment) => (
              <ListItem key={comment.id}>
                <ActivityItem item={comment} topicId={feedback.id} />
              </ListItem>
            ))}
            <ListItem />
          </List>
        </Scrollbars>
      </Box>

      <Box px="6" mb="4">
        <ComentCompose handleSubmit={addComment} />
      </Box>
    </Flex>
  );
};
